import React, { useState, useEffect } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

const DashboardTutorial = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: "50%",
    left: "50%",
  });

  const tutorialSteps = [
    {
      title: "Welcome to Your Dashboard! 🎉",
      description:
        "Let's take a quick tour of your dashboard features to help you get started.",
      target: null,
      position: "center",
    },
    {
      title: "CRM/ERP Toggle",
      description:
        "Switch between CRM (Customer Relationship Management) and ERP (Enterprise Resource Planning) views using this toggle. Your preference is saved automatically.",
      target: ".view-toggle",
      position: "below",
    },
    {
      title: "Key Metrics Cards",
      description:
        "Quick overview of your most important metrics at a glance. These update in real-time based on your selected view.",
      target: ".metrics-grid",
      position: "below",
    },
    {
      title: "Sales Funnel Visualization",
      description:
        "Track your sales pipeline stages with this interactive funnel chart. Hover over segments for detailed information.",
      target: ".funnel-summary-grid",
      position: "below",
    },
    {
      title: "Conversion Insights",
      description:
        "Monitor your conversion rates, rejection rates, and active pipeline status here.",
      target: ".summary-stats",
      position: "below",
    },
    {
      title: "Detailed Charts",
      description:
        "Dive deeper into your data with these interactive charts. They provide breakdowns of enquiry status, client distribution, and more.",
      target: ".charts-grid",
      position: "below",
    },
    {
      title: "Recent Activity",
      description:
        "Stay updated with the latest activities and interactions in your pipeline.",
      target: ".activity-section",
      position: "above",
    },
    {
      title: "You're All Set! ✨",
      description:
        "You can always refresh the data or switch views using the controls at the top. Happy managing!",
      target: null,
      position: "center",
    },
  ];

  const currentStepData = tutorialSteps[currentStep];

  useEffect(() => {
    const updateHighlightAndPosition = () => {
      // Remove previous highlights
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        el.classList.remove("tutorial-highlight");
        el.style.position = "";
        el.style.zIndex = "";
        el.style.boxShadow = "";
        el.style.borderRadius = "";
      });

      if (currentStepData.target) {
        const element = document.querySelector(currentStepData.target);

        if (element) {
          // Add highlight class
          element.classList.add("tutorial-highlight");

          // Apply highlight styles
          const originalPosition = window.getComputedStyle(element).position;
          if (originalPosition === "static") {
            element.style.position = "relative";
          }
          element.style.zIndex = "10001";
          element.style.boxShadow =
            "0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 0 8px rgba(59, 130, 246, 0.3)";
          element.style.borderRadius = "12px";
          element.style.transition = "all 0.3s ease";

          // Calculate tooltip position
          const rect = element.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

          let newPosition = {};

          if (currentStepData.position === "below") {
            newPosition = {
              position: "absolute",
              top: `${rect.bottom + scrollTop + 20}px`,
              left: `${rect.left + scrollLeft + rect.width / 2}px`,
              transform: "translateX(-50%)",
            };
          } else if (currentStepData.position === "above") {
            newPosition = {
              position: "absolute",
              top: `${rect.top + scrollTop - 20}px`,
              left: `${rect.left + scrollLeft + rect.width / 2}px`,
              transform: "translate(-50%, -100%)",
            };
          }

          setTooltipPosition(newPosition);

          // Scroll into view with offset
          const yOffset = -100;
          const y = rect.top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        } else {
          console.warn(`Element not found: ${currentStepData.target}`);
          // If element not found, show tooltip in center
          setTooltipPosition({
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          });
        }
      } else {
        // Center position for intro/outro
        setTooltipPosition({
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      }
    };

    // Small delay to ensure DOM is ready
    updateHighlightAndPosition();

    return () => {
      // Cleanup highlights
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        el.classList.remove("tutorial-highlight");
        el.style.position = "";
        el.style.zIndex = "";
        el.style.boxShadow = "";
        el.style.borderRadius = "";
      });
    };
  }, [currentStep, currentStepData]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Clean up any highlights
    document.querySelectorAll(".tutorial-highlight").forEach((el) => {
      el.classList.remove("tutorial-highlight");
      el.style.position = "";
      el.style.zIndex = "";
      el.style.boxShadow = "";
      el.style.borderRadius = "";
    });
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 10000,
          backdropFilter: "blur(2px)",
        }}
        onClick={handleSkip}
      />

      {/* Tutorial Tooltip */}
      <div
        style={{
          ...tooltipPosition,
          zIndex: 10002,
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
          padding: "24px",
          maxWidth: "420px",
          width: "90vw",
          animation: "fadeInScale 0.3s ease-out",
          pointerEvents: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "8px",
              }}
            >
              {currentStepData.title}
            </h3>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                fontWeight: "600",
              }}
            >
              Step {currentStep + 1} of {tutorialSteps.length}
            </div>
          </div>
          <button
            onClick={handleSkip}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
              e.currentTarget.style.color = "#111827";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div
          style={{
            height: "4px",
            backgroundColor: "#e5e7eb",
            borderRadius: "2px",
            marginBottom: "20px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              backgroundColor: "#3b82f6",
              width: `${((currentStep + 1) / tutorialSteps.length) * 100}%`,
              transition: "width 0.3s ease",
            }}
          />
        </div>

        {/* Content */}
        <p
          style={{
            margin: 0,
            fontSize: "15px",
            lineHeight: "1.6",
            color: "#4b5563",
            marginBottom: "24px",
          }}
        >
          {currentStepData.description}
        </p>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            style={{
              padding: "10px 20px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: "white",
              color: "#6b7280",
              cursor: currentStep === 0 ? "not-allowed" : "pointer",
              opacity: currentStep === 0 ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (currentStep !== 0) {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.borderColor = "#d1d5db";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <div style={{ display: "flex", gap: "12px" }}>
            {currentStep < tutorialSteps.length - 1 && (
              <button
                onClick={handleSkip}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  background: "white",
                  color: "#6b7280",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                Skip Tour
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: "10px 24px",
                border: "none",
                borderRadius: "8px",
                background: " #3b82f6 ",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "600",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(59, 130, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(59, 130, 246, 0.3)";
              }}
            >
              {currentStep === tutorialSteps.length - 1
                ? "Get Started"
                : "Next"}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .tutorial-highlight {
          animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 0 8px rgba(59, 130, 246, 0.3) !important;
          }
          50% {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.7), 0 0 0 8px rgba(59, 130, 246, 0.5) !important;
          }
        }
      `}</style>
    </>
  );
};

// Main wrapper component that checks localStorage
const DashboardWithTutorial = ({ children }) => {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Check if user has seen the tutorial
    const hasSeenTutorial = localStorage.getItem("dashboardTutorialCompleted");
    if (!hasSeenTutorial) {
      // Delay to let the dashboard render first
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem("dashboardTutorialCompleted", "true");
  };

  return (
    <>
      {children}
      {showTutorial && <DashboardTutorial onClose={handleCloseTutorial} />}
    </>
  );
};

export default DashboardWithTutorial;
